import axios from "axios";

/**
 * Web integration API client (iframe / guest flow).
 *
 * ACCESS + REFRESH LIFECYCLE (no logout UI here yet)
 * -------------------------------------------------
 * 1. After OTP verification, `setWebIntegrationAuthSnapshot` stores `accessToken` and
 *    `refreshToken`. Protected calls attach `Authorization: Bearer <accessToken>`.
 *
 * 2. When the access token expires, the next protected request typically returns 401.
 *    The response interceptor below calls `refresh_access_token/` with the current
 *    `refreshToken`, then updates `authSnapshot` with the new access (and refresh if
 *    the API rotates it) and retries the failed request once (`_wiRetry`).
 *
 * 3. That pattern repeats: whenever access expires again, the next 401 triggers the same
 *    refresh path, as long as `refreshToken` is still present and the refresh call succeeds.
 *
 * 4. If refresh fails (e.g. refresh token expired/revoked) or there is no `refreshToken`,
 *    the interceptor rejects the error — callers see the failure. Explicit logout /
 *    clearing tokens and redirect UX can be added later (e.g. `setWebIntegrationAuthSnapshot`
 *    with empty tokens).
 */

/** Dedicated client for refresh — avoids mixing with default `axios` interceptors */
const refreshAxios = axios.create();

const rawBase =
  process.env.REACT_APP_BACKEND_ROUTE_URL_WEB_INTEGRATION || "";
export const WEB_INTEGRATION_BASE_URL = rawBase
  .replace(/['"\s]/g, "")
  .replace(/([^/])$/, "$1/");

/** Endpoints that must not send Org-Code / App-Mode */
const NO_ORG_HEADERS = ["organization_validation/"];

/**
 * Paths that must not send Bearer (pre-auth guest steps + refresh endpoint itself).
 * All other paths, once the user has an access token, get `Authorization: Bearer …`.
 */
const NO_BEARER = [
  "organization_validation/",
  "get_organization_info/",
  "patient_guest_registration/",
  "send_mobile_otp/",
  "verify_mobile_otp/",
  "refresh_access_token/",
];

/** In-memory tokens/org headers for this flow (not sessionStorage). */
let authSnapshot = {
  orgCode: "",
  appMode: "",
  accessToken: "",
  refreshToken: "",
};

export function setWebIntegrationAuthSnapshot(next) {
  authSnapshot = { ...authSnapshot, ...next };
}

export function getWebIntegrationAuthSnapshot() {
  return { ...authSnapshot };
}

export const webIntegrationAxios = axios.create({
  baseURL: WEB_INTEGRATION_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

webIntegrationAxios.interceptors.request.use(
  (config) => {
    if (!config.headers) {
      config.headers = {};
    }
    const url = config.url || "";
    const fullUrl = `${config.baseURL || ""}${url}`;

    const skipOrg = NO_ORG_HEADERS.some((p) => fullUrl.includes(p));
    if (!skipOrg && authSnapshot.orgCode) {
      config.headers["ORG-CODE"] = authSnapshot.orgCode;
      config.headers["APP-MODE"] = authSnapshot.appMode;
    }

    const needsBearer = !NO_BEARER.some((p) => fullUrl.includes(p));
    if (needsBearer && authSnapshot.accessToken) {
      config.headers.Authorization = `Bearer ${authSnapshot.accessToken}`;
    }

    return config;
  },
  (err) => Promise.reject(err)
);

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newAccess) {
  refreshSubscribers.forEach((cb) => cb(newAccess));
  refreshSubscribers = [];
}

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

/** Calls refresh API with Bearer omitted; uses ORG-CODE / APP-MODE when org is known. */
async function postRefresh(refreshToken) {
  const { orgCode, appMode } = authSnapshot;
  const res = await refreshAxios.post(
    `${WEB_INTEGRATION_BASE_URL}refresh_access_token/`,
    { refresh: refreshToken },
    {
      headers: {
        "Content-Type": "application/json",
        ...(orgCode ? { "ORG-CODE": orgCode, "APP-MODE": appMode } : {}),
      },
    }
  );
  return res;
}

/**
 * On 401 for a Bearer-protected URL: try one refresh + retry.
 * - Concurrent 401s while refresh is in flight queue and reuse the new access token.
 * - If refresh returns no access, or throws: propagate error (no logout handling here).
 * - `_wiRetry` prevents infinite refresh loops on the same request.
 */
webIntegrationAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest || originalRequest._wiRetry) {
      return Promise.reject(error);
    }

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const url = `${originalRequest.baseURL || ""}${originalRequest.url || ""}`;
    const needsBearer = !NO_BEARER.some((p) => url.includes(p));
    if (!needsBearer) {
      return Promise.reject(error);
    }

    const refreshToken = authSnapshot.refreshToken;
    if (!refreshToken) {
      // No way to obtain a new access token; caller must handle (e.g. future logout).
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newAccess) => {
          if (!newAccess) {
            reject(error);
            return;
          }
          originalRequest._wiRetry = true;
          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
          resolve(webIntegrationAxios(originalRequest));
        });
      });
    }

    originalRequest._wiRetry = true;
    isRefreshing = true;

    try {
      const res = await postRefresh(refreshToken);
      const data = res.data || {};
      const access =
        data.access || data.data?.access || data.data?.access_token;
      const refresh =
        data.refresh || data.data?.refresh || data.data?.refresh_token;

      if (access) {
        setWebIntegrationAuthSnapshot({
          accessToken: access,
          ...(refresh ? { refreshToken: refresh } : {}),
        });
        onRefreshed(access);
        isRefreshing = false;
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return webIntegrationAxios(originalRequest);
      }
      isRefreshing = false;
      onRefreshed(null);
      return Promise.reject(error);
    } catch (e) {
      isRefreshing = false;
      onRefreshed(null);
      return Promise.reject(e);
    }
  }
);
